import { defineModule, localActionContext, localGetterContext } from 'direct-vuex'
import type { DocumentNode } from 'graphql'
import {
  ConfirmationTokenType,
  HaftarahMelodyType,
  Mutation,
  PronunciationType,
  Query,
  SentenceGroupMelodyType,
  TagType,
  TorahMelodyType,
  UserGrapheneType,
} from 'types/types'
import type { ActionContext } from 'vuex'

import { UpdateUserParameters } from '@/composables/util'
import { LS_IMPERSONATOR_KEY, LS_THEME_KEY, LS_USER_KEY } from '@/constants/practiceConstants'
import Contact from '@/mutations/Contact.graphql'
import ForgotUserPassword from '@/mutations/ForgotUserPassword.graphql'
import Impersonate from '@/mutations/Impersonate.graphql'
import Login from '@/mutations/Login.graphql'
import Logout from '@/mutations/Logout.graphql'
import UpdateTheme from '@/mutations/UpdateTheme.graphql'
import UpdateUser from '@/mutations/UpdateUser.graphql'
import UpdateUserPassword from '@/mutations/UpdateUserPassword.graphql'
import UpdateUserPasswordFromSecretToken from '@/mutations/UpdateUserPasswordFromSecretToken.graphql'
import ConfirmationTokenQuery from '@/queries/ConfirmationTokenQuery.graphql'
import UserQuery from '@/queries/UserQuery.graphql'
import { MUTATION_FAILED_MESSAGE, MutationPayload, ReportingMutation, runReportedMutation, showStoreError } from '@/store/mutationHelpers'
import { useApollo } from '@/util/apolloClient'
import { filterVisibleTags } from '@/util/Tags'
import { cloneRaw, normalizeEmail, normalizeUsername } from '@/util/util'
const { apolloClient } = useApollo()

export interface UserState {
  user: UserGrapheneType | null
  impersonator: UserGrapheneType | null
  impersonatorUrl: string | null
  authenticating: boolean
  confirmationToken: ConfirmationTokenType | null
  updating: boolean
  // keep an eye on sanitize() when adding fields
}

const resetState = (): UserState => {
  return {
    authenticating: false,
    confirmationToken: null,
    impersonator: null,
    impersonatorUrl: null,
    updating: false,
    user: null,
    // keep an eye on sanitize() when adding fields
  }
}

/** Parse a JSON value from localStorage, returning null on missing/corrupt data and clearing the key. */
function getStoredJSON<T>(key: string): T | null {
  try {
    const raw = localStorage[key]
    return raw ? JSON.parse(raw) : null
  } catch {
    console.error(`Corrupt ${key} in localStorage — clearing`)
    localStorage.removeItem(key)
    return null
  }
}

// --- Shared helper for mutation actions ---

interface UserMutationOptions<K extends ReportingMutation> {
  /** Mutation that flags the request as in flight while it runs. */
  loading?: 'setAuthenticating' | 'setUpdating'
  /** Runs after the server accepted the mutation. Resolve false to report the mutation as failed anyway. */
  onSuccess?: (payload: MutationPayload<K>) => boolean | Promise<boolean>
  /** Where to show a failure; defaults to the snackbar. */
  showError?: (message: string) => void
}

/**
 * Run a user-initiated GraphQL mutation. Resolves true on success. On failure it shows the reason
 * in the snackbar and resolves false; it never rejects.
 */
async function runUserMutation<K extends ReportingMutation>(
  context: ActionContext<UserState, unknown>,
  mutation: DocumentNode,
  variables: Record<string, unknown>,
  dataKey: K,
  { loading, onSuccess, showError = (message) => showStoreError(context, message) }: UserMutationOptions<K> = {},
): Promise<boolean> {
  // eslint-disable-next-line no-use-before-define
  const { commit } = userActionContext(context)
  if (loading) {
    commit[loading](true)
  }
  try {
    const payload = await runReportedMutation(mutation, variables, dataKey, showError)
    if (!payload) {
      return false
    }
    if ((await onSuccess?.(payload)) === false) {
      showError(MUTATION_FAILED_MESSAGE)
      return false
    }
    return true
  } finally {
    if (loading) {
      commit[loading](false)
    }
  }
}

/** Reload the user after a saved change. The change stands even if the reload fails (fetchUser logs it). */
async function refreshUser(context: ActionContext<UserState, unknown>): Promise<boolean> {
  // eslint-disable-next-line no-use-before-define
  const { dispatch } = userActionContext(context)
  try {
    await dispatch.fetchUser()
  } catch {
    // already logged by fetchUser
  }
  return true
}

const user = defineModule({
  actions: {
    clearImpersonator(context) {
      const { commit } = userActionContext(context)
      commit.clearImpersonator()
    },

    contact(context, payload: { email: string; message: string }): Promise<boolean> {
      return runUserMutation(context, Contact, { email: payload.email, message: payload.message }, 'contact', { loading: 'setUpdating' })
    },

    async fetchConfirmationToken(context, payload: { token: string }) {
      const { commit } = userActionContext(context)
      try {
        const response = await apolloClient.query<Pick<Query, 'confirmationToken'>>({
          fetchPolicy: 'network-only',
          query: ConfirmationTokenQuery,
          variables: {
            token: payload.token,
          },
        })
        const token = response.data?.confirmationToken
        if (token) {
          commit.setConfirmationToken(token)
        }
      } catch (error) {
        console.error('fetchConfirmationToken failed:', error)
      }
    },

    fetchUser(context) {
      const { commit } = userActionContext(context)
      return new Promise<void>((resolve, reject) => {
        apolloClient
          .query<Pick<Query, 'user'>>({
            fetchPolicy: 'network-only',
            query: UserQuery,
          })
          .then((response) => {
            const user = response.data?.user
            if (user) {
              commit.setUser(user)
            }
            resolve()
          })
          .catch((error) => {
            console.error('fetchUser failed:', error)
            reject(error)
          })
      })
    },

    forgotUserPassword(context, payload: { usernameOrEmail: string }): Promise<boolean> {
      return runUserMutation(
        context,
        ForgotUserPassword,
        { usernameOrEmail: normalizeEmail(payload.usernameOrEmail) },
        'forgotUserPassword',
        { loading: 'setUpdating' },
      )
    },

    /** Log in. A failure goes to `showError` when given (the login form shows it inline), otherwise to the snackbar. */
    login(context, payload: { username: string; password: string; showError?: (message: string) => void }): Promise<boolean> {
      const { commit } = userActionContext(context)
      return runUserMutation(context, Login, { password: payload.password, username: normalizeUsername(payload.username) }, 'login', {
        loading: 'setAuthenticating',
        onSuccess: ({ user }) => {
          if (!user) {
            // ok was true but user is null — treat as failure
            return false
          }
          commit.clearImpersonator()
          commit.setUser(user)
          return true
        },
        showError: payload.showError,
      })
    },

    // alias for reset
    /** Tell the backend to end the session. Does NOT reset client state — callers handle that via doLogout(). */
    async logout() {
      try {
        const response = await apolloClient.mutate<Pick<Mutation, 'logout'>>({
          mutation: Logout,
          variables: { username: 'dummy' },
        })
        const ok = response.data?.logout?.ok
        console.log('response.data.logout, ok =', ok)
        return !!ok
      } catch (error) {
        console.log('store.user.logout error:', error)
        return false
      }
    },

    reset(context) {
      const { commit } = userActionContext(context)
      commit.reset()
    },

    sanitize(context) {
      const { commit } = userActionContext(context)
      commit.sanitize()
    },

    setImpersonator(context, user: UserGrapheneType) {
      const { commit } = userActionContext(context)
      commit.setImpersonator(user)
    },

    setImpersonatorUrl(context, url: string) {
      const { commit } = userActionContext(context)
      commit.setImpersonatorUrl(url)
    },

    setUser(context, user: UserGrapheneType) {
      const { commit } = userActionContext(context)
      commit.setUser(user)
    },

    setUserPassword(context, payload: { secretToken: string; newPassword: string }): Promise<boolean> {
      const mutation = payload.secretToken ? UpdateUserPasswordFromSecretToken : UpdateUserPassword
      const variables: { newPassword: string; secretToken?: string } = { newPassword: payload.newPassword }
      if (payload.secretToken) {
        variables.secretToken = payload.secretToken
      }
      const dataKey: keyof Mutation = payload.secretToken ? 'updateUserPasswordFromSecretToken' : 'updateUserPassword'
      return runUserMutation(context, mutation, variables, dataKey, { loading: 'setUpdating' })
    },

    startImpersonate(context, payload: { username: string }): Promise<boolean> {
      const { commit } = userActionContext(context)
      return runUserMutation(context, Impersonate, { username: normalizeUsername(payload.username) }, 'impersonate', {
        onSuccess: ({ user }) => {
          if (!user) {
            return false
          }
          commit.setImpersonation(user)
          return true
        },
      })
    },

    unsetImpersonate(context) {
      const { commit } = userActionContext(context)
      commit.unsetImpersonation()
    },

    /** Save the theme and refresh the user. Resolves false (and shows why) on failure. */
    updateTheme(context, payload: { theme: string }): Promise<boolean> {
      return runUserMutation(context, UpdateTheme, { theme: payload.theme }, 'updateTheme', {
        loading: 'setUpdating',
        onSuccess: () => refreshUser(context),
      })
    },

    /** Save the profile and refresh the user. Resolves false (and shows why) on failure. */
    updateUser(context, payload: UpdateUserParameters): Promise<boolean> {
      return runUserMutation(
        context,
        UpdateUser,
        {
          font: payload.user.properties?.font,
          fontSize: payload.user.properties?.fontSize,
          gender: payload.user.properties?.gender,
          haftarahMelodies: payload.haftarahMelodyIDs,
          kamatzKatanColor: payload.user.properties?.kamatzKatanColor || null,
          lineHeight: payload.user.properties?.lineHeight,
          pronunciations: payload.pronunciationIDs,
          sentenceGroupMelodies: payload.sentenceGroupMelodyIDs,
          shewaNaColor: payload.user.properties?.shewaNaColor || null,
          tags: payload.tags,
          torahMelodies: payload.torahMelodyIDs,
          userData: {
            email: normalizeEmail(payload.user.email),
            firstName: payload.user.firstName,
            lastName: payload.user.lastName,
            username: normalizeUsername(payload.user.username),
          },
        },
        'updateUser',
        { loading: 'setUpdating', onSuccess: () => refreshUser(context) },
      )
    },
  },
  getters: {
    authenticating(...args): boolean {
      const { state } = userGetterContext(args)
      return state.authenticating
    },

    confirmationToken(...args): ConfirmationTokenType | null {
      const { state } = userGetterContext(args)
      return state.confirmationToken
    },

    impersonator(...args): UserGrapheneType | null {
      const { state } = userGetterContext(args)
      return state.impersonator ?? getStoredJSON<UserGrapheneType>(LS_IMPERSONATOR_KEY)
    },

    impersonatorUrl(...args): string {
      const { state } = userGetterContext(args)
      return state.impersonatorUrl ?? ''
    },

    updating(...args): boolean {
      const { state } = userGetterContext(args)
      return state.updating
    },

    user(...args): UserGrapheneType | null {
      const { state } = userGetterContext(args)
      return state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
    },

    /** The user's preferred font family, or undefined if not set. */
    userFont(...args): string | undefined {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return u?.properties?.font
    },

    /** The user's preferred font size. */
    userFontSize(...args): number | undefined {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return u?.properties?.fontSize
    },

    /** The user's preferred gender (bar/bat/b), or undefined if not set. */
    userGender(...args): string | undefined {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return u?.properties?.gender
    },

    userGroupNames(...args): string[] {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return (u?.groups ?? []).map((g) => g.name)
    },

    userHaftarahMelodies(...args): HaftarahMelodyType[] {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return (u?.haftarahMelodies ?? []).map((h) => h.melody)
    },

    userIsLoggedIn(...args): boolean {
      const { state } = userGetterContext(args)
      return !!(state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY))
    },

    /** Whether the user has read-only access. */
    userIsReadOnly(...args): boolean {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return u?.properties?.readOnly ?? false
    },

    /** Whether the user is a staff member (Django is_staff flag). */
    userIsStaff(...args): boolean {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return !!u?.isStaff
    },

    /** The user's preferred line height, or undefined if not set. */
    userLineHeight(...args): number | undefined {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return u?.properties?.lineHeight
    },

    userPronunciations(...args): PronunciationType[] {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return (u?.pronunciations ?? []).map((p) => p.pronunciation)
    },

    userSentenceGroupMelodies(...args): SentenceGroupMelodyType[] {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return (u?.sentenceGroupMelodies ?? []).map((h) => h.melody)
    },

    userTorahMelodies(...args): TorahMelodyType[] {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return (u?.torahMelodies ?? []).map((m) => m.melody)
    },

    userUsername(...args): string {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return u?.username ?? ''
    },

    userVisibleTags(...args): TagType[] {
      const { state } = userGetterContext(args)
      const u = state.user ?? getStoredJSON<UserGrapheneType>(LS_USER_KEY)
      return u?.tags ? [...filterVisibleTags(u.tags)].sort((a, b) => a.name.localeCompare(b.name)) : []
    },
  },

  mutations: {
    clearImpersonator(state: UserState) {
      state.impersonator = null
      localStorage.removeItem(LS_IMPERSONATOR_KEY)
    },

    reset(state: UserState) {
      console.log('store.user reset state')
      localStorage.removeItem(LS_USER_KEY)
      localStorage.removeItem(LS_IMPERSONATOR_KEY)
      Object.assign(state, { ...resetState() })
    },

    // clean non-user/impersonator fields
    sanitize(state: UserState) {
      state.authenticating = false
      state.confirmationToken = null
      state.updating = false
    },

    setAuthenticating(state: UserState, authenticating: boolean) {
      state.authenticating = authenticating
    },

    setConfirmationToken(state: UserState, confirmationTokenX: ConfirmationTokenType) {
      state.confirmationToken = confirmationTokenX
    },

    setImpersonation(state: UserState, userx: UserGrapheneType) {
      // Save original user as impersonator BEFORE switching user
      state.impersonator = cloneRaw(state.user)
      localStorage[LS_IMPERSONATOR_KEY] = JSON.stringify(state.user)
      // Then switch to impersonated user
      state.user = cloneRaw(userx)
      localStorage[LS_USER_KEY] = JSON.stringify(userx)
    },

    setImpersonator(state: UserState, userx: UserGrapheneType) {
      state.impersonator = cloneRaw(userx)
      localStorage[LS_IMPERSONATOR_KEY] = JSON.stringify(userx)
    },

    setImpersonatorUrl(state: UserState, url: string) {
      state.impersonatorUrl = url
    },

    setUpdating(state: UserState, updating: boolean) {
      state.updating = updating
    },

    setUser(state: UserState, userx: UserGrapheneType) {
      state.user = cloneRaw(userx)
      console.log('state.user is now')
      console.log(state.user)
      localStorage[LS_USER_KEY] = JSON.stringify(userx)
      localStorage[LS_THEME_KEY] = userx.properties?.theme
    },

    unsetImpersonation(state: UserState) {
      if (!state.impersonator) {
        console.error('WARNING: unsetImpersonation called with no impersonator — aborting to avoid losing user')
        return
      }
      state.user = cloneRaw(state.impersonator)
      localStorage[LS_USER_KEY] = JSON.stringify(state.user)
      state.impersonator = null
      localStorage.removeItem(LS_IMPERSONATOR_KEY)
    },
  },

  namespaced: true as const,

  state: resetState(),
})

export default user
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const userGetterContext = (args: [any, any, any, any]) => localGetterContext(args, user)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const userActionContext = (context: any) => localActionContext(context, user)
